// Cenários de orçamento do Gestão e do Studio = páginas de cenário do Planejamento.
//
// GET /orcamentos/{syncId}?ano= devolve { itens, cenarios }: cada item traz o valor de cada página em
// `cenarios` (chave = id da página) e `cenarios` lista as páginas ({ id, nome, legacyKey, anos }).
//
// A escolha do cenário (budgetValueType) guarda o id da página. Configurações antigas guardam
// 'valorMedio' | 'valorPessimista' | 'valorOtimista' (orçamento do Sync): continuam valendo pela página
// migrada correspondente (legacyKey), mesmo depois de renomeada; sem ela, valem pelo primeiro cenário.

export const DEFAULT_BUDGET_VALUE_TYPE = 'valorMedio';

export const LEGACY_BUDGET_TYPES = Object.freeze({
  valorMedio: 'medio',
  valorPessimista: 'pessimista',
  valorOtimista: 'otimista',
});

const isLegacyType = (type) => Object.prototype.hasOwnProperty.call(LEGACY_BUDGET_TYPES, type);

const asList = (scenarios) => (Array.isArray(scenarios) ? scenarios : []);

/** Página escolhida (id) para a lista de cenários da empresa; null quando não há cenário. */
export const resolveBudgetScenarioId = (type, scenarios) => {
  const list = asList(scenarios);
  const value = type || DEFAULT_BUDGET_VALUE_TYPE;
  if (isLegacyType(value)) {
    const migrated = list.find((s) => s.legacyKey === LEGACY_BUDGET_TYPES[value]);
    return migrated?.id || list[0]?.id || null;
  }
  return value;
};

/** Nome do cenário que a escolha aplica (null quando não há cenário). */
export const budgetScenarioName = (type, scenarios) => {
  const id = resolveBudgetScenarioId(type, scenarios);
  return asList(scenarios).find((s) => s.id === id)?.nome || null;
};

/** Valor do orçamento de um item no cenário (id da página já resolvido por resolveBudgetScenarioId). */
export const budgetEntryValue = (entry, scenarioId) => {
  if (!entry || !scenarioId) return 0;
  const byScenario = entry.cenarios || entry.Cenarios;
  if (!byScenario) return 0;
  return Number(byScenario[scenarioId] ?? byScenario[String(scenarioId).toLowerCase()]) || 0;
};

/** Cenários que vieram com as respostas do orçamento (uma por ano): a primeira lista não vazia. */
export const budgetScenariosFromResponses = (responses) => {
  for (const res of asList(responses)) {
    const list = res?.cenarios ?? res?.Cenarios;
    if (Array.isArray(list) && list.length) return list;
  }
  return [];
};

const yearsLabel = (anos) => {
  const list = asList(anos);
  if (!list.length) return 'sem plano salvo';
  return list.length === 1 ? `plano de ${list[0]}` : `planos de ${list.join(', ')}`;
};

/**
 * Opções do seletor de cenário, com os anos que cada cenário tem plano. O valor selecionado é sempre
 * um id de página: uma configuração antiga aparece como a página que ela resolve; um cenário excluído
 * aparece desabilitado.
 */
export const budgetScenarioOptions = (scenarios, currentType) => {
  const options = asList(scenarios).map((s) => ({ label: `${s.nome} · ${yearsLabel(s.anos)}`, value: s.id }));
  const current = resolveBudgetScenarioId(currentType, scenarios);
  if (current && !options.some((o) => o.value === current)) {
    options.push({ label: 'Cenário excluído', value: current, disabled: true });
  }
  return { options, value: current || undefined };
};

/**
 * Aviso quando o cenário aplicado não tem plano em nenhum dos anos exibidos (o orçamento sairia zerado
 * sem explicação). null quando há plano em algum ano exibido ou quando não há o que avisar.
 */
export const budgetScenarioYearsNotice = (type, scenarios, displayedYears) => {
  const list = asList(scenarios);
  const years = [...new Set(asList(displayedYears).map(Number).filter(Number.isFinite))].sort((a, b) => a - b);
  if (!years.length) return null;
  if (!list.length) return 'Nenhum cenário de orçamento nesta empresa. Crie em Planejamento › Simulador.';
  const id = resolveBudgetScenarioId(type, list);
  const scenario = list.find((s) => s.id === id);
  if (!scenario) return 'O cenário de orçamento escolhido foi excluído. Escolha outro na configuração.';
  const planned = asList(scenario.anos).map(Number);
  if (years.some((y) => planned.includes(y))) return null;
  const shown = years.join(', ');
  return planned.length
    ? `O cenário "${scenario.nome}" não tem plano de ${shown} (tem ${planned.join(', ')}). O orçamento fica zerado.`
    : `O cenário "${scenario.nome}" ainda não tem plano salvo. O orçamento fica zerado.`;
};
